import { FC, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguageSelectorContainer, SelectedValue, DropdownList, DropdownItem } from './language-selector.styled'

const LANGUAGES: Record<string, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  ru: 'Русский',
  sv: 'Svenska',
  tr: 'Türkçe',
  ja: '日本語',
  ko: '한국어',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'zh-CN': '中文(简体)',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'zh-TW': '中文(繁體)',
  th: 'ภาษาไทย',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  fil: 'Filipino',
  ar: 'العربية',
}

const GlobeIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2"/>
    <ellipse cx="8" cy="8" rx="2.5" ry="6.5" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="1.5" y1="6" x2="14.5" y2="6" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="1.5" y1="10" x2="14.5" y2="10" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
)

const ChevronIcon: FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}
  >
    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const LanguageSelector: FC = () => {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const currentLang = i18n.resolvedLanguage ?? 'en'
  const currentName = LANGUAGES[currentLang] ?? currentLang

  const handleSelect = (lang: string) => {
    try { localStorage.setItem('yuno-lng', lang) } catch { /* storage unavailable */ }
    i18n.changeLanguage(lang)
    setIsOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <LanguageSelectorContainer ref={containerRef} onClick={() => setIsOpen(prev => !prev)}>
      <GlobeIcon />
      <SelectedValue>{currentName}</SelectedValue>
      <ChevronIcon isOpen={isOpen} />
      <DropdownList isOpen={isOpen}>
        {Object.entries(LANGUAGES).map(([code, name]) => (
          <DropdownItem
            key={code}
            isSelected={code === currentLang}
            onClick={(e) => { e.stopPropagation(); handleSelect(code) }}
          >
            {name}
          </DropdownItem>
        ))}
      </DropdownList>
    </LanguageSelectorContainer>
  )
}
